/*
 * /classes/:slug/concepts/:subId? — Learning Packages: chọn Package (subId = package id),
 * xem trải nghiệm cá nhân hoá, bắt đầu/tiếp tục (chế độ tập trung), so sánh 3 trải nghiệm.
 */
import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { motion, useReducedMotion } from "framer-motion"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { Icon } from "@/components/icon"
import { EmptyState, ErrorState, Page, PageHeader, StatusNote, usePageTitle } from "@/components/page"
import { learningTypeLabel } from "@/components/learning"
import { useClass, useFocusMode } from "@/app/ClassLayout"
import { api, post } from "@/lib/api"
import { LearningExperiencePlayer } from "./concepts/player"
import { LearningExperienceComparison } from "./concepts/comparison"

function StepItem({ step }: { step: any }) {
  return (
    <li className="flex gap-4 rounded-2xl border-[1.5px] border-line bg-card p-4">
      <span className="grid size-9 shrink-0 place-items-center rounded-full bg-soft font-heading font-bold text-orange-strong">{step.order}</span>
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <h3 className="font-heading text-base font-semibold text-ink">{step.title}</h3>
          <span className="flex items-center gap-1 text-sm text-muted-foreground">
            <Icon name="clock" className="size-4" /> {step.duration_minutes} phút
          </span>
        </div>
        <p className="text-sm text-ink">{step.instruction}</p>
        {step.support && <p className="text-sm text-muted-foreground">{step.support}</p>}
        {step.evidence && (
          <Collapsible>
            <CollapsibleTrigger className="group flex items-center gap-1 text-sm font-semibold text-orange-strong">
              Bằng chứng cần tạo <Icon name="chevron-down" className="size-4 transition-transform group-data-panel-open:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <p className="pt-2 text-sm text-muted-foreground">{step.evidence}</p>
            </CollapsibleContent>
          </Collapsible>
        )}
      </div>
    </li>
  )
}

export default function ConceptsPage() {
  usePageTitle("Concepts", "Learning Package cá nhân hoá theo Learner Model của từng bạn.")
  const { slug, base } = useClass()
  const { subId } = useParams()
  const navigate = useNavigate()
  const reduce = useReducedMotion()
  const [data, setData] = useState<any>()
  const [error, setError] = useState("")
  const [activeExperienceId, setActiveExperienceId] = useState("")
  const [comparison, setComparison] = useState<any>()
  const [busy, setBusy] = useState("")
  const [message, setMessage] = useState("")

  const load = () => {
    setError("")
    return api(`/api/classes/${encodeURIComponent(slug)}/learning-packages`)
      .then((next) => {
        setData(next)
        return next
      })
      .catch((e: any) => {
        setError(e.message)
        throw e
      })
  }
  useEffect(() => {
    setData(undefined)
    load().catch(() => {})
  }, [slug])

  const packages: any[] = data?.packages || []
  const selected = packages.find((p) => p.id === subId) || packages[0]
  const playing = !!selected && activeExperienceId === selected.experience_id
  useFocusMode(playing)

  const pick = (id: string) => {
    setActiveExperienceId("")
    setComparison(undefined)
    setMessage("")
    navigate(`${base}/concepts/${encodeURIComponent(id)}`)
  }

  const start = async () => {
    setBusy(selected.id)
    setMessage("")
    try {
      await post(`/api/classes/${encodeURIComponent(slug)}/learning-packages`, { package_id: selected.id, experience_id: selected.experience_id })
      await load()
      setActiveExperienceId(selected.experience_id)
      window.scrollTo({ top: 0, behavior: "smooth" })
    } catch (e: any) {
      setMessage(e.message)
    } finally {
      setBusy("")
    }
  }

  const compare = async () => {
    const path = `/api/classes/${encodeURIComponent(slug)}/learning-packages/${encodeURIComponent(selected.id)}/comparison`
    setBusy("comparison")
    setMessage("")
    setComparison(undefined)
    try {
      const queued: any = await post(path, {})
      if (queued?.result) return setComparison(queued.result)
      if (!queued?.workflow_id) throw new Error("Không tạo được bản so sánh")
      for (let attempt = 0; attempt < 30; attempt += 1) {
        await new Promise((r) => setTimeout(r, attempt === 0 ? 300 : 1000))
        const status: any = await api(`${path}?workflow_id=${encodeURIComponent(queued.workflow_id)}`)
        if (status?.result) return setComparison(status.result)
      }
      throw new Error("Workflow đang mất nhiều thời gian hơn dự kiến. Con thử lại nhé.")
    } catch (e: any) {
      setMessage(e.message)
    } finally {
      setBusy("")
    }
  }

  if (playing)
    return (
      <LearningExperiencePlayer
        slug={slug}
        selected={selected}
        onReload={load}
        onBack={() => {
          setActiveExperienceId("")
          window.scrollTo({ top: 0, behavior: "smooth" })
        }}
      />
    )

  const header = <PageHeader eyebrow="Learning" title="Concepts" description="Cùng một Concept, Simba tạo trải nghiệm học khác nhau theo Learner Model của từng bạn." />

  if (error)
    return (
      <Page>
        {header}
        <ErrorState message={error} onRetry={() => load().catch(() => {})} />
      </Page>
    )
  if (!data)
    return (
      <Page>
        {header}
        <p className="text-sm text-muted-foreground">Simba đang tạo trải nghiệm phù hợp với con…</p>
        <Skeleton className="h-14 rounded-2xl" />
        <Skeleton className="h-80 rounded-[20px]" />
      </Page>
    )
  if (!selected)
    return (
      <Page>
        {header}
        <EmptyState mascot="giraffe-thinking" title="Chưa có Learning Package phù hợp" description="Capos chưa xuất bản Package cho chủ đề này." />
      </Page>
    )

  const experience = selected.experience || {}
  const steps: any[] = experience.steps || []
  const typeKey = selected.knowledge_type || selected.type
  const items = packages.map((p) => ({ value: p.id, label: p.title }))

  return (
    <Page>
      {header}

      {packages.length > 1 && (
        <div className="flex flex-col gap-2 sm:max-w-md">
          <span className="text-sm font-semibold text-ink">Chọn Learning Package</span>
          <Select items={items} value={selected.id} onValueChange={(v) => v && pick(String(v))}>
            <SelectTrigger className="h-11 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {items.map((it) => (
                <SelectItem key={it.value} value={it.value}>
                  {it.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <motion.div key={selected.id} initial={reduce ? false : { y: 8 }} animate={{ y: 0 }} transition={{ duration: 0.22 }}>
        <Card>
          <CardContent className="flex flex-col gap-6">
            <header className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary">Capos preview · {selected.code}</Badge>
                {typeKey && learningTypeLabel[typeKey] && <Badge>{learningTypeLabel[typeKey]}</Badge>}
                {experience.mode_label && <Badge variant="solid">{experience.mode_label}</Badge>}
              </div>
              <h2 className="text-2xl leading-tight font-bold sm:text-3xl">{selected.title}</h2>
              {selected.summary && <p className="text-base text-muted-foreground">{selected.summary}</p>}
            </header>

            <section className="flex gap-4 rounded-2xl bg-soft p-4 sm:p-5">
              <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-card">
                <Icon name="sparkles" className="size-5 text-orange" />
              </span>
              <div className="flex min-w-0 flex-col gap-1">
                <p className="text-sm font-bold tracking-[0.08em] text-orange-strong uppercase">Trải nghiệm của riêng con</p>
                <strong className="text-base text-ink">{experience.opening}</strong>
                {experience.reason && <p className="text-sm text-muted-foreground">{experience.reason}</p>}
              </div>
            </section>

            <ol className="flex flex-col gap-3">
              {steps.map((step) => (
                <StepItem key={step.id} step={step} />
              ))}
            </ol>

            <footer className="flex flex-col gap-3 border-t-[1.5px] border-line pt-5 sm:flex-row sm:items-center sm:justify-between">
              <span className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon name="clock" className="size-4" /> {experience.estimated_minutes} phút
              </span>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button variant="outline" size="lg" disabled={!!busy} onClick={compare}>
                  <Icon name="sparkles" className="size-4" />
                  {busy === "comparison" ? "Đang tạo 3 phiên bản…" : "So sánh 3 trải nghiệm"}
                </Button>
                <Button size="lg" disabled={!!busy} onClick={start}>
                  {busy === selected.id ? "Đang mở…" : selected.last_opened_at ? "Tiếp tục trải nghiệm" : "Bắt đầu trải nghiệm"}
                  <Icon name="arrow-right" className="size-4" />
                </Button>
              </div>
            </footer>
            <StatusNote tone="error">{message}</StatusNote>
            <p className="text-sm text-muted-foreground">
              Package đang ở trạng thái {selected.lifecycle_status}/{selected.quality_status}. Đây là bản thử nghiệm, chưa phải kết quả hoàn thành chính thức.
            </p>
          </CardContent>
        </Card>
      </motion.div>

      {comparison && <LearningExperienceComparison comparison={comparison} onClose={() => setComparison(undefined)} />}
    </Page>
  )
}
